import { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import Sidebar from '@/components/Sidebar';
import NoteList from '@/components/NoteList';
import TrashView from '@/components/TrashView';
import MainEditor from '@/components/MainEditor';
import AuthModal from '@/components/AuthModal';
import UpgradeBanner from '@/components/UpgradeBanner';
import MobileTopBar from '@/components/MobileTopBar';
import { useSelector } from '@legendapp/state/react';

// Lazy load OnboardingModal — only shown once for new users
const OnboardingModal = lazy(() => import('@/components/OnboardingModal'));
import { auth$ } from '@/stores/auth$';
import { ui$ } from '@/stores/ui$';
import { notes$ } from '@/stores/notes$';
import { refreshSharedTagsCache } from '@/stores/sharedTags$';
import { supabase } from '@/lib/supabaseClient';
import { initFeaturebase, identifyFeaturebaseUser, clearFeaturebaseIdentity, updateFeaturebaseTheme, resolveFeaturebaseTheme } from '@/lib/featurebase';
import {
  initPostHog,
  initGA4,
  initHotjar,
  captureCrossDomainParams,
  identifyUser as identifyAnalyticsUser,
  clearUserIdentity as clearAnalyticsIdentity,
  trackEvent,
  setOnceUserProperties,
  trackSessionStart,
} from '@/lib/analytics';
import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import PwaInstall from '@khmyznikov/pwa-install/dist/pwa-install.react';
import { ErrorBoundary } from 'react-error-boundary';
import { EditorErrorFallback } from '@/components/ErrorFallback';
import { Sentry } from '@/lib/sentry';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useRouteSync } from '@/hooks/useRouteSync';
import { useEmptyNoteCleanup } from '@/hooks/useEmptyNoteCleanup';

const App = () => {
  const theme = useSelector(() => ui$.theme.get());
  const viewMode = useSelector(() => ui$.viewMode.get());
  const activeNoteId = useSelector(() => ui$.activeNoteId.get());
  const activeNote = useSelector(() => activeNoteId ? notes$[activeNoteId]?.get() : null);
  const onboardingCompleted = useSelector(() => ui$.onboardingCompleted.get());
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Generate dynamic page title based on active note
  const pageTitle = activeNote?.title
    ? `${activeNote.title} - PromptNote`
    : 'PromptNote';

  // PWA theme-color management
  useThemeColor();

  // Bidirectional sync between URL and UI state (enables browser back, deep links)
  useRouteSync();

  // Auto-delete a note the user leaves without ever using it (empty-note cleanup)
  useEmptyNoteCleanup();

  // console.log('[App] Render - theme:', theme, 'isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);

  // Initialize analytics services
  useEffect(() => {
    initPostHog();
    initGA4();
    initHotjar();

    // Capture cross-domain tracking from promptnote-site
    captureCrossDomainParams();
  }, []);

  // Initialize auth state
  useEffect(() => {
    // console.log('[App] useEffect - initializing auth');

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // console.log('[App] getSession result:', session ? 'has session' : 'no session');
      if (session) {
        // Update Legend State store
        auth$.user.set({
          id: session.user.id,
          username: session.user.user_metadata?.username || session.user.email?.split('@')[0],
          email: session.user.email,
        });
        auth$.session.set(session);
        auth$.isAuthenticated.set(true);
        setIsAuthenticated(true);
      }
      setIsLoading(false);
      // console.log('[App] After getSession - setting isLoading to false');
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // console.log('[App] onAuthStateChange:', event, session ? 'has session' : 'no session');

      if (session) {
        auth$.user.set({
          id: session.user.id,
          username: session.user.user_metadata?.username || session.user.email?.split('@')[0],
          email: session.user.email,
        });
        auth$.session.set(session);
        auth$.isAuthenticated.set(true);
        setIsAuthenticated(true);
        // console.log('[App] Set authenticated to TRUE');
      } else {
        auth$.user.set(null);
        auth$.session.set(null);
        auth$.isAuthenticated.set(false);
        setIsAuthenticated(false);
        // console.log('[App] Set authenticated to FALSE');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Theme handling
  useEffect(() => {
    // console.log('[App] Theme changed to:', theme);
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      // console.log('[App] System theme detected:', systemTheme);
      root.classList.add(systemTheme);
    } else {
      // console.log('[App] Applying theme:', theme);
      root.classList.add(theme);
    }
  }, [theme]);

  // Initialize Featurebase feedback widget with current theme
  useEffect(() => {
    const currentTheme = ui$.theme.get();
    const featurebaseTheme = resolveFeaturebaseTheme(currentTheme);
    initFeaturebase({ theme: featurebaseTheme });

    // Restore "hide feedback" state if still within the 30-min window
    try {
      const hiddenUntil = parseInt(localStorage.getItem('promptnote-feedback-hidden-until'), 10);
      if (hiddenUntil && Date.now() < hiddenUntil) {
        document.body.classList.add('feedback-temporarily-hidden');
        const remaining = hiddenUntil - Date.now();
        setTimeout(() => document.body.classList.remove('feedback-temporarily-hidden'), remaining);
      } else {
        localStorage.removeItem('promptnote-feedback-hidden-until');
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  // Sync Featurebase widget theme with app theme changes
  useEffect(() => {
    // Update Featurebase theme when app theme changes
    const featurebaseTheme = resolveFeaturebaseTheme(theme);
    updateFeaturebaseTheme(featurebaseTheme);
  }, [theme]);

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemChange = () => {
      const featurebaseTheme = resolveFeaturebaseTheme('system');
      updateFeaturebaseTheme(featurebaseTheme);
    };
    mediaQuery.addEventListener('change', handleSystemChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemChange);
    };
  }, [theme]);

  // Identify user in Featurebase and analytics when authenticated
  useEffect(() => {
    const user = auth$.user.get();
    if (isAuthenticated && user && user.email) {
      // Only identify users with email (skip anonymous users)
      identifyFeaturebaseUser(user.id, {
        email: user.email,
        name: user.username || user.email?.split('@')[0],
      });

      // Identify in analytics services (PostHog, GA4, Hotjar)
      identifyAnalyticsUser(user.id, {
        email: user.email,
        username: user.username || user.email?.split('@')[0],
      });

      // Track first login (fires only once via setOnce)
      setOnceUserProperties({ first_login_at: new Date().toISOString() });
      trackEvent('first_login', { user_id: user.id });

      // Session tracking (session_started, D1/D7 return)
      const session = auth$.session.get();
      trackSessionStart({
        id: user.id,
        created_at: session?.user?.created_at,
      });
    } else if (!isLoading && !isAuthenticated) {
      // Clear identity when logged out
      clearFeaturebaseIdentity();
      clearAnalyticsIdentity();
    }
  }, [isAuthenticated, isLoading]);

  // Initialize shared tags cache when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshSharedTagsCache();
    }
  }, [isAuthenticated]);

  // PWA install prompt
  const isPromptnotePwaDismissed = () => {
    try {
      const until = parseInt(localStorage.getItem('promptnote-pwa-dismissed'), 10);
      return until && Date.now() < until;
    } catch { return false; }
  };
  const showPwaInstall = !isLoading && isAuthenticated && !Capacitor.isNativePlatform() && !isPromptnotePwaDismissed();
  const handlePromptnotePwaChoice = () => {
    try {
      localStorage.setItem('promptnote-pwa-dismissed', String(Date.now() + 30 * 24 * 60 * 60 * 1000));
    } catch {}
  };

  // Derive onboarding visibility (no local state needed)
  const showOnboarding = !isLoading && isAuthenticated && !onboardingCompleted;

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-dvh w-full items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Always show main app, but overlay AuthModal if not authenticated
  return (
    <div className="flex flex-col h-dvh w-full bg-background text-foreground overflow-hidden font-sans">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {/* Wrapper: applies mobile header offset once, so columns don't need individual pt */}
      <div className="flex flex-col flex-1 overflow-hidden pt-[var(--mobile-header-h)] md:pt-0">
        {/* Upgrade Banner for anonymous users */}
        <UpgradeBanner />

        {/* Main App Container */}
        <div className="flex flex-1 overflow-hidden">
          {/* Mobile Top Bar */}
          <MobileTopBar />

        {/* 3-Column Layout (responsive) */}
        <Sidebar />
        {viewMode === 'trash' ? <TrashView /> : <NoteList />}
        <ErrorBoundary
          FallbackComponent={EditorErrorFallback}
          resetKeys={[activeNoteId]}
          onError={(error, info) => {
            console.error('[ErrorBoundary] Editor:', error, info.componentStack);
            Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
          }}
        >
          <MainEditor />
        </ErrorBoundary>
        </div>
      </div>

      {/* Onboarding Modal - lazy loaded, shows for first-time authenticated users */}
      <Suspense fallback={null}>
        {showOnboarding && <OnboardingModal isOpen={showOnboarding} onClose={() => {}} />}
      </Suspense>

      {/* Auth Modal - shows as overlay when not authenticated */}
      {!isAuthenticated && <AuthModal />}

      {showPwaInstall && (
        <PwaInstall
          manifestUrl="/manifest.webmanifest"
          onPwaUserChoiceResultEvent={handlePromptnotePwaChoice}
          onPwaInstallSuccessEvent={handlePromptnotePwaChoice}
        />
      )}
    </div>
  );
};

export default App;
